import { Chip, FormControlLabel, Radio, RadioGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useStyles } from "./registrant-selection.style";
import PruTableLoading from "src/app/common/components/Table/PruTableLoading";
import PruTableEmptyRow from "src/app/common/components/Table/PruTableEmptyRow";
import { alpha } from '@mui/material/styles';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import React, { useMemo } from "react";
import { SearchRegistrationExamineeResponseItem } from 'src/app/modules/Recruitment/types/license-exam-types';
import { useTranslation } from "@pruforce/common-adminweb-sdk";


export interface RegistrantResultTableProps {
  resultList: SearchRegistrationExamineeResponseItem[];
  searching: boolean;
  selectedRegistrantId: string;
  onChange: (row: SearchRegistrationExamineeResponseItem) => void;
}

interface RegistrantResultTableContext {
  selectableTableRowClassName: string;
  searching: boolean;
  selectedRegistrantId: string;
  onChange: (row: SearchRegistrationExamineeResponseItem) => void;
}

interface ColumnData {
  width?: number;
  translationKey?: string;
  renderLabel?: () => string;
  renderCell: (row: SearchRegistrationExamineeResponseItem) => React.ReactNode;
}

const columns: ColumnData[] = [
  {
    width: 200,
    translationKey: "recruitment.exam.registration.adminRegistration.registrantSelection.candidateIdAndAgentCode",
    renderCell: (row) => row.examineeId,
  },
  {
    translationKey: "enName",
    renderCell: (row) => row.enName,
  },
  {
    translationKey: "localName",
    renderCell: (row) => row.localName,
  },
  {
    width: 130,
    translationKey: "recruitment.exam.registration.adminRegistration.registrantSelection.accountType",
    renderCell: (row) => row.accountType,
  },
  {
    width: 120,
    translationKey: "recruiterCode",
    renderCell: (row) => row.recruiterCode,
  },
  {
    width: 150,
    translationKey: "recruiterName",
    renderCell: (row) => row.recruiterName,
  },
  {
    width: 150,
    translationKey: "recruitment.exam.registration.adminRegistration.registrantSelection.doceboAccountStatus",
    renderCell: (row) => row.accountStatus,
  },
];

const getColumnLabel = (column: ColumnData, t: any) => {
  if (column.renderLabel) {
    return column.renderLabel();
  }

  return t(column.translationKey || '');
};

const createVirtuosoTableComponents = (tableClassName: string, emptyClassName: string): TableComponents<SearchRegistrationExamineeResponseItem, RegistrantResultTableContext> => ({
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer {...props} ref={ref} />
  )),
  Table: (props) => (
    <Table {...props} className={tableClassName} size="small" />
  ),
  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow: React.forwardRef<HTMLTableRowElement, React.ComponentPropsWithoutRef<typeof TableRow> & { item: SearchRegistrationExamineeResponseItem; context: RegistrantResultTableContext }>((props, ref) => {
    const { context, item, className, ...rest } = props;
    return (
      <TableRow
        {...rest}
        ref={ref}
        hover
        selected={context.selectedRegistrantId === item.examineeId}
        onClick={() => !context.searching && context.onChange(item)}
        className={[className, context.selectableTableRowClassName].filter(Boolean).join(' ')}
      />
    );
  }),
  TableBody: React.forwardRef<HTMLTableSectionElement, React.ComponentPropsWithoutRef<typeof TableBody>>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
  EmptyPlaceholder: ({ context }) => (
    <TableBody className={emptyClassName}>
      <PruTableLoading isLoading={context.searching} />
      <PruTableEmptyRow isEmpty />
    </TableBody>
  ),
});

const fixedHeaderContent = (tableHeaderCellClassName: string, isLoading = false) => {
  const t = useTranslation();
  return (
    <>
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.translationKey || column.width || getColumnLabel(column, t)}
            variant="head"
            width={column.width}
            className={tableHeaderCellClassName}
          >
            {getColumnLabel(column, t)}
          </TableCell>
        ))}
      </TableRow>
      {isLoading && <PruTableLoading isLoading />}
    </>
  );
};

const TABLE_HEADER_HEIGHT = 50;
const TABLE_ROW_HEIGHT = 40;
const TABLE_EMPTY_HEIGHT = 90;
const TABLE_DEFAULT_HEIGHT = 'clamp(150px, 35vh, 420px)';

export const RegistrantResultTable = (props: RegistrantResultTableProps) => {
  const { resultList, searching, selectedRegistrantId, onChange } = props;
  const { classes } = useStyles();
  const virtuosoTableComponents = React.useMemo(() =>
    createVirtuosoTableComponents(classes.resultTable, classes.emptyTableBody), [classes.resultTable, classes.emptyTableBody]);
  const resultRadioGroupClassName = resultList.length === 0
    ? `${classes.resultRadioGroup} ${classes.resultRadioGroupEmpty}`
    : classes.resultRadioGroup;

  const itemContent = (_index: number, row: SearchRegistrationExamineeResponseItem) => {
    return (
      <>
        <TableCell className={classes.tableBodyCell}>
          <div className={classes.candidateCellContent}>
            <FormControlLabel
              className={classes.candidateRadioLabel}
              label={row.examineeId}
              value={row.examineeId}
              control={
                <Radio
                  size="small"
                  color="secondary"
                  className={classes.candidateRadio}
                  checked={selectedRegistrantId === row.examineeId}
                  disabled={searching}
                  onChange={() => onChange(row)}
                />
              }
            />
          </div>
        </TableCell>
        {columns.slice(1).map((column, index) => (
          <TableCell key={`${row.examineeId}-${column.translationKey || index}`} className={classes.tableBodyCell}>
            {index === columns.length - 2 ? (
              <Chip
                variant="filled"
                size="small"
                label={row.accountStatus}
                sx={(theme) => ({
                  backgroundColor: alpha(theme.palette.success.main, 0.2),
                  color: theme.palette.success.dark,
                  fontWeight: 500,
                })}
              />
            ) : (
              column.renderCell(row)
            )}
          </TableCell>
        ))}
      </>
    );
  };

  const wrapperStyle = useMemo(() => {
    const rowCount = resultList.length;

    if (rowCount > 5) {
      return { height: TABLE_DEFAULT_HEIGHT };
    }

    return {
      height:
        TABLE_HEADER_HEIGHT +
        Math.max(rowCount * TABLE_ROW_HEIGHT, TABLE_EMPTY_HEIGHT),
    };
  }, [resultList.length]);

  return (
    <div className={classes.resultTableWrapper}>
      <RadioGroup
        className={resultRadioGroupClassName}
        style={wrapperStyle}
        value={selectedRegistrantId}>
        <TableVirtuoso
          data={resultList}
          components={virtuosoTableComponents}
          context={{ searching, selectedRegistrantId, onChange, selectableTableRowClassName: classes.selectableTableRow }}
          fixedHeaderContent={() => fixedHeaderContent(classes.tableHeaderCell, searching && resultList.length > 0)}
          itemContent={itemContent}
        />
      </RadioGroup>
    </div>
  );
};
